import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, RefreshCw, AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import ImportQuantitiesButton from './ImportQuantitiesButton';
import { can } from '../../services/session';
import AddMappingModal from './AddMappingModal';
import { PLATFORM_CONFIG } from './platformConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

const PLATFORM_META = {
  shopify: { label: 'Shopify', color: '#3B8E59' },
  ebay:    { label: 'eBay',    color: '#3A6EC8' },
  amazon:  { label: 'Amazon',  color: '#E78C2E' },
};

// Combined, cross-platform view of every SKU mapping. From here you can push a
// stock quantity to every mapped platform/store at once, or to a single one.
// Push SETS (overrides) the quantity, same as the per-platform pages.
// One physical listing should map to exactly one master product. eBay is the
// one legitimate exception — a single Item ID covers many variations, each
// disambiguated by its own variation_sku — so eBay only collides when BOTH
// the item id and the variation_sku match. Everywhere else, the platform SKU
// alone must be unique.
function collisionKey(m) {
  return m.platform === 'ebay'
    ? `ebay|${m.store_id}|${m.platform_sku}|${m.variation_sku || ''}`
    : `${m.platform}|${m.store_id ?? 'null'}|${m.platform_sku}`;
}

export default function CombinedSkuView() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [multiOnly, setMultiOnly] = useState(false);
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const [qtyEdits, setQtyEdits] = useState({});       // productId -> quantity being typed
  const [pushingRow, setPushingRow] = useState(null); // productId currently pushing "all"
  const [pushingMap, setPushingMap] = useState(null); // mapping id currently pushing
  const [addMappingFor, setAddMappingFor] = useState(null); // { platform, product } | null
  const [storesByPlatform, setStoresByPlatform] = useState({ ebay: [], amazon: [] });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setProducts(data.products);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Store lists for the "add mapping" modal — only eBay/Amazon have per-store SKUs.
  useEffect(() => {
    const token = localStorage.getItem('token');
    for (const platform of ['ebay', 'amazon']) {
      fetch(`${API_BASE_URL}${PLATFORM_CONFIG[platform].storeApi}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => { if (d.success) setStoresByPlatform(prev => ({ ...prev, [platform]: d.stores })); })
        .catch(() => {});
    }
  }, []);

  const qtyFor = (p) => (qtyEdits[p.id] !== undefined ? qtyEdits[p.id] : (p.quantity ?? 0));

  // update one mapping's live_quantity badge in place after a push
  const patchLive = (mappingIds, newQty) => {
    setProducts(ps => ps.map(p => ({
      ...p,
      mappings: (p.mappings || []).map(m => mappingIds.includes(m.id) ? { ...m, live_quantity: newQty } : m),
    })));
  };

  const pushAll = async (product) => {
    const quantity = parseInt(qtyFor(product), 10) || 0;
    setPushingRow(product.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products/${product.id}/push-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity }),
      });
      const data = await res.json();
      if (!data.success) { toast.error(data.error || 'Push failed'); return; }
      const ok = data.results.filter(r => r.success);
      const bad = data.results.filter(r => !r.success && !r.skipped);
      patchLive((product.mappings || []).map(m => m.id), quantity);
      toast.success(`${product.master_sku}: updated ${ok.length}/${data.results.length} listings to ${quantity}${bad.length ? `, ${bad.length} failed` : ''}`);
      bad.forEach(r => toast.error(`${PLATFORM_META[r.platform]?.label || r.platform} ${r.store_name || r.sku || ''}: ${r.error}`, { autoClose: 9000 }));
      setProducts(ps => ps.map(p => p.id === product.id ? { ...p, quantity } : p));
      setQtyEdits(e => { const n = { ...e }; delete n[product.id]; return n; });
    } catch { toast.error('Push failed'); }
    finally { setPushingRow(null); }
  };

  const pushOne = async (product, mapping) => {
    const quantity = parseInt(qtyFor(product), 10) || 0;
    setPushingMap(mapping.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/mappings/${mapping.id}/push-quantity`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity }),
      });
      const data = await res.json();
      if (!data.success) { toast.error(data.error || 'Push failed'); return; }
      const r = data.result;
      if (r.success) {
        patchLive([mapping.id], quantity);
        toast.success(`${r.store_name || PLATFORM_META[r.platform]?.label}: updated to ${quantity}`);
      } else {
        toast.error(`${r.store_name || r.sku}: ${r.error}`, { autoClose: 9000 });
      }
    } catch { toast.error('Push failed'); }
    finally { setPushingMap(null); }
  };

  // Every mapping, grouped by its "real listing" key — anything with more than
  // one distinct master SKU behind the same key means that one physical
  // listing is currently bound to two different master products.
  const collisionGroups = useMemo(() => {
    const map = {};
    for (const p of products) {
      for (const m of (p.mappings || [])) {
        if (!m.platform_sku) continue;
        const key = collisionKey(m);
        (map[key] ||= []).push({ master_sku: p.master_sku, product_name: p.product_name, platform: m.platform, hasVariation: !!m.variation_sku });
      }
    }
    const conflicts = {};
    for (const [key, entries] of Object.entries(map)) {
      const skus = [...new Set(entries.map(e => e.master_sku))];
      if (skus.length <= 1) continue;
      const names = new Set(entries.map(e => e.product_name));

      let severity;
      if (names.size <= 1) {
        // Same product name across every colliding SKU = almost certainly the
        // same real item entered twice under two SKU naming conventions — untidy
        // but low-risk.
        severity = 'duplicate';
      } else if (entries[0].platform === 'ebay' && entries.every(e => !e.hasVariation)) {
        // eBay legitimately lets one Item ID cover many colour/size variations —
        // if NONE of the colliding rows has ever had a variation_sku recorded,
        // this reads as a real multi-variation listing that's simply missing its
        // per-variation SKUs, not proof the mapping itself is wrong.
        severity = 'incomplete';
      } else {
        // Different products, and either not eBay (where one SKU = one real
        // listing, no variation concept) or eBay with a mix of recorded/missing
        // variation_sku — a genuine stock-mixup risk, not cosmetic.
        severity = 'critical';
      }
      conflicts[key] = { entries, severity };
    }
    return conflicts;
  }, [products]);

  const conflictInfoFor = (m) => collisionGroups[collisionKey(m)] || null;

  const totalConflicts = useMemo(() => Object.keys(collisionGroups).length, [collisionGroups]);
  const criticalConflicts = useMemo(
    () => Object.values(collisionGroups).filter(c => c.severity === 'critical').length,
    [collisionGroups]
  );
  const incompleteConflicts = useMemo(
    () => Object.values(collisionGroups).filter(c => c.severity === 'incomplete').length,
    [collisionGroups]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return products
      .map(p => {
        // Searching by master SKU or product name means "find this product" —
        // show its full real picture across every platform. Searching by a
        // specific platform SKU means "find this exact listing" — show only
        // the mapping(s) that actually matched, dashing out the rest of the
        // row even though the product is mapped elsewhere too.
        const matchedByProductInfo = !q
          || p.master_sku.toLowerCase().includes(q)
          || (p.product_name || '').toLowerCase().includes(q);

        const visibleMappings = (!q || matchedByProductInfo)
          ? (p.mappings || [])
          : (p.mappings || []).filter(m =>
              (m.platform_sku || '').toLowerCase().startsWith(q) ||
              (m.variation_sku || '').toLowerCase().startsWith(q)
            );

        const byPlatform = { shopify: [], ebay: [], amazon: [] };
        for (const m of visibleMappings) {
          if (byPlatform[m.platform]) byPlatform[m.platform].push(m);
        }
        const platformCount = ['shopify', 'ebay', 'amazon'].filter(pl => byPlatform[pl].length > 0).length;
        const hasConflict = (p.mappings || []).some(m => collisionGroups[collisionKey(m)]);
        return { ...p, byPlatform, platformCount, storeCount: (p.mappings || []).length, hasConflict, matched: matchedByProductInfo || visibleMappings.length > 0 };
      })
      .filter(r => r.storeCount > 0)
      .filter(r => !multiOnly || r.platformCount >= 2)
      .filter(r => !conflictsOnly || r.hasConflict)
      .filter(r => r.matched);
  }, [products, search, multiOnly, conflictsOnly, collisionGroups]);

  const multiPlatformTotal = useMemo(
    () => products.filter(p => new Set((p.mappings || []).map(m => m.platform)).size >= 2).length,
    [products]
  );
  const mappedTotal = useMemo(
    () => products.filter(p => (p.mappings || []).length > 0).length,
    [products]
  );

  const cell = (product, platform, mappings) => {
    if (!mappings.length) {
      return (
        <button
          onClick={() => setAddMappingFor({ platform, product })}
          title={`Add a ${PLATFORM_CONFIG[platform].label} mapping for this product`}
          className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-white"
        >
          <Plus size={11} /> Add
        </button>
      );
    }
    return (
      <div className="space-y-1.5">
        {mappings.map(m => {
          // eBay stores the SKU in variation_sku and the numeric listing Item ID
          // in platform_sku — show the SKU as the primary text like every other
          // platform does, with the Item ID as a small reference underneath.
          const isEbay = m.platform === 'ebay';
          const primary = isEbay ? (m.variation_sku || m.platform_sku) : m.platform_sku;
          const secondary = isEbay && m.variation_sku ? m.platform_sku : null;
          const conflict = conflictInfoFor(m);
          const otherSkus = conflict ? [...new Set(conflict.entries.map(e => e.master_sku))].filter(s => s !== product.master_sku) : [];
          const sev = conflict?.severity; // 'critical' | 'duplicate' | 'incomplete'
          const sevStyle = {
            critical:   { ring: 'ring-1 ring-red-500/50 bg-red-500/10 px-1 py-0.5 -mx-1',    text: 'text-red-400',   label: 'CONFLICT',   title: 'DIFFERENT products' },
            duplicate:  { ring: 'ring-1 ring-amber-500/40 bg-amber-500/10 px-1 py-0.5 -mx-1', text: 'text-amber-400', label: 'dup',         title: 'Same product, duplicate SKU' },
            incomplete: { ring: 'ring-1 ring-sky-500/40 bg-sky-500/10 px-1 py-0.5 -mx-1',     text: 'text-sky-400',   label: 'needs variation SKU', title: 'Likely a shared multi-variation eBay listing — not proven wrong, just missing per-variation SKUs' },
          }[sev];
          return (
          <div key={m.id} className={`flex items-start gap-1.5 leading-tight rounded ${sevStyle ? sevStyle.ring : ''}`}>
            <div className="min-w-0">
              {m.store_name && <span className="block text-[10px] text-gray-500 truncate">{m.store_name}</span>}
              <span className="font-mono text-xs text-gray-200 break-all">{primary}</span>
              {m.live_quantity != null && (
                <span className={`ml-1 text-[10px] ${Number(m.live_quantity) === 0 ? 'text-red-400' : 'text-gray-400'}`}>({m.live_quantity})</span>
              )}
              {secondary && <span className="block font-mono text-[10px] text-gray-600 break-all">Item {secondary}</span>}
              {sevStyle && (
                <span
                  className="flex items-center gap-1 mt-0.5"
                  title={`${sevStyle.title} — this exact listing is also mapped to: ${otherSkus.join(', ')}`}
                >
                  <AlertTriangle size={10} className={`shrink-0 ${sevStyle.text}`} />
                  <span className={`text-[10px] break-all ${sevStyle.text}`}>
                    {sevStyle.label} also on {otherSkus.join(', ')}
                  </span>
                </span>
              )}
            </div>
            <button
              onClick={() => pushOne(product, m)}
              disabled={pushingMap === m.id || pushingRow === product.id}
              title="Push the quantity to this listing only"
              className="shrink-0 text-gray-500 hover:text-white disabled:opacity-40 mt-0.5"
            >
              <RefreshCw size={11} className={pushingMap === m.id ? 'animate-spin' : ''} />
            </button>
          </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 text-gray-500" size={15} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search master SKU, name, or platform SKU…"
            className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white/30"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none">
          <input type="checkbox" checked={multiOnly} onChange={e => setMultiOnly(e.target.checked)} />
          On 2+ platforms only
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
          <input type="checkbox" checked={conflictsOnly} onChange={e => setConflictsOnly(e.target.checked)} />
          <span className={totalConflicts > 0 ? 'text-amber-400' : 'text-gray-300'}>
            Conflicts only {totalConflicts > 0 && `(${totalConflicts})`}
          </span>
        </label>
        <span className="text-xs text-gray-400">
          {loading ? '…' : `${rows.length} shown · ${mappedTotal} mapped · ${multiPlatformTotal} multi-platform`}
        </span>
        {can('sku_mapping', 'edit') && <ImportQuantitiesButton platform="all" label="Import Quantities (All Platforms)" onDone={fetchProducts} dark />}
      </div>
      {totalConflicts > 0 && !conflictsOnly && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300 flex items-start gap-2">
          <AlertTriangle size={13} className="shrink-0 mt-0.5" />
          <span>
            {totalConflicts} listing{totalConflicts !== 1 ? 's are' : ' is'} bound to more than one master product —{' '}
            {criticalConflicts > 0 && (
              <span className="text-red-300 font-medium">{criticalConflicts} confirmed <span className="uppercase">CONFLICT</span> (genuinely different products sharing one SKU, real risk), </span>
            )}
            {incompleteConflicts > 0 && (
              <span className="text-sky-300 font-medium">{incompleteConflicts} likely fine but incomplete (eBay multi-variation listings missing per-variation SKUs, not proven wrong), </span>
            )}
            <span className="text-amber-300">{totalConflicts - criticalConflicts - incompleteConflicts} benign dup (same product, two legacy SKU codes).</span>
            {' '}Check "Conflicts only" above to review them.
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 text-sm py-8 text-center">
          <RefreshCw className="animate-spin mx-auto mb-2" size={20} /> Loading mappings…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-gray-500 text-sm py-8 text-center">No mappings match.</div>
      ) : (
        <div className="overflow-x-auto border border-white/10 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/5 text-left">
                <th className="py-2 px-3 text-gray-400 font-medium text-xs">Master SKU</th>
                <th className="py-2 px-3 text-gray-400 font-medium text-xs">Product</th>
                {['shopify', 'ebay', 'amazon'].map(pl => (
                  <th key={pl} className="py-2 px-3 font-medium text-xs" style={{ color: PLATFORM_META[pl].color }}>
                    {PLATFORM_META[pl].label}
                  </th>
                ))}
                <th className="py-2 px-3 text-gray-400 font-medium text-xs text-right">Push stock</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-white/5 align-top hover:bg-white/5">
                  <td className="py-2 px-3">
                    <span className="font-mono text-xs font-semibold text-white bg-white/10 px-1.5 py-0.5 rounded">{r.master_sku}</span>
                    {r.platformCount >= 2 && (
                      <span className="block mt-1 text-[10px] text-emerald-400">{r.platformCount} platforms</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-gray-300 text-xs max-w-[200px]">
                    <span className="line-clamp-2">{r.product_name}</span>
                  </td>
                  <td className="py-2 px-3">{cell(r, 'shopify', r.byPlatform.shopify)}</td>
                  <td className="py-2 px-3">{cell(r, 'ebay', r.byPlatform.ebay)}</td>
                  <td className="py-2 px-3">{cell(r, 'amazon', r.byPlatform.amazon)}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <input
                        type="number"
                        min="0"
                        value={qtyFor(r)}
                        onChange={e => setQtyEdits(prev => ({ ...prev, [r.id]: parseInt(e.target.value) || 0 }))}
                        className="w-16 px-1.5 py-1 text-xs text-center bg-white/10 border border-white/15 rounded text-white [color-scheme:dark]"
                      />
                      <button
                        onClick={() => pushAll(r)}
                        disabled={pushingRow === r.id}
                        title="Push this quantity to every mapped platform & store"
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-white/10 hover:bg-white/20 text-white rounded disabled:opacity-40"
                      >
                        <RefreshCw size={11} className={pushingRow === r.id ? 'animate-spin' : ''} />
                        All
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addMappingFor && (
        <AddMappingModal
          platform={addMappingFor.platform}
          product={addMappingFor.product}
          stores={storesByPlatform[addMappingFor.platform]}
          onClose={() => setAddMappingFor(null)}
          onSaved={fetchProducts}
        />
      )}
    </div>
  );
}
